import { Fragment } from 'react'
import type { ReactNode } from 'react'

/**
 * Renders the backend's prose answer.
 *
 * Built as React elements rather than `dangerouslySetInnerHTML`: the answer is
 * LLM-generated text derived from database content, so it is exactly the kind
 * of string that must never be parsed as HTML. React escapes every text node
 * here by construction.
 *
 * The supported subset is narrow on purpose — `_clean_answer` in
 * `app/api/query.py` already strips asterisks, so what actually arrives is
 * paragraphs, numbered lists from `_multiple_rows_answer`, and the occasional
 * dash bullet.
 */

const INLINE_CODE = /`([^`]+)`/g

function renderInline(text: string, keyPrefix: string): ReactNode[] {
  const nodes: ReactNode[] = []
  let lastIndex = 0
  let match: RegExpExecArray | null
  let index = 0

  INLINE_CODE.lastIndex = 0
  while ((match = INLINE_CODE.exec(text)) !== null) {
    if (match.index > lastIndex) {
      nodes.push(text.slice(lastIndex, match.index))
    }
    nodes.push(
      <code
        key={`${keyPrefix}-code-${index}`}
        className="rounded border border-line bg-sunken px-1 py-0.5 font-mono text-[0.92em]"
      >
        {match[1]}
      </code>,
    )
    lastIndex = match.index + match[0].length
    index += 1
  }

  if (lastIndex < text.length) nodes.push(text.slice(lastIndex))
  return nodes.length ? nodes : [text]
}

function renderWithBreaks(text: string, keyPrefix: string): ReactNode[] {
  return text.split('\n').flatMap((line, index) => {
    const content = renderInline(line, `${keyPrefix}-${index}`)
    return index === 0
      ? content
      : [<br key={`${keyPrefix}-br-${index}`} />, ...content]
  })
}

const BULLET = /^\s*[-•]\s+/
const ORDERED = /^\s*\d+[.)]\s+/

type Block =
  | { kind: 'paragraph'; text: string }
  | { kind: 'bullets' | 'ordered'; items: string[] }

function parseBlocks(answer: string): Block[] {
  return answer
    .split(/\n{2,}/)
    .map((chunk) => chunk.trim())
    .filter(Boolean)
    .flatMap<Block>((chunk) => {
      const lines = chunk.split('\n')

      if (lines.every((line) => BULLET.test(line))) {
        return [{ kind: 'bullets', items: lines.map((l) => l.replace(BULLET, '')) }]
      }

      if (lines.every((line) => ORDERED.test(line))) {
        return [{ kind: 'ordered', items: lines.map((l) => l.replace(ORDERED, '')) }]
      }

      // Mixed block: a lead-in sentence followed by an enumerated list is what
      // `_multiple_rows_answer` produces, so split it rather than flattening.
      const firstListIndex = lines.findIndex(
        (line) => BULLET.test(line) || ORDERED.test(line),
      )

      if (firstListIndex > 0) {
        const lead = lines.slice(0, firstListIndex).join('\n')
        const listLines = lines.slice(firstListIndex)
        const ordered = ORDERED.test(listLines[0] ?? '')

        return [
          { kind: 'paragraph', text: lead },
          {
            kind: ordered ? 'ordered' : 'bullets',
            items: listLines.map((line) => line.replace(ordered ? ORDERED : BULLET, '')),
          },
        ]
      }

      return [{ kind: 'paragraph', text: chunk }]
    })
}

export function AnswerText({ answer }: { answer: string }) {
  const text = (answer ?? '').trim()

  if (!text) {
    return (
      <p className="text-sm text-subtle italic">
        The server returned no answer text.
      </p>
    )
  }

  const blocks = parseBlocks(text)

  return (
    <div className="space-y-3 text-sm leading-relaxed text-fg">
      {blocks.map((block, index) => {
        if (block.kind === 'paragraph') {
          return (
            <p key={index}>{renderWithBreaks(block.text, `p-${index}`)}</p>
          )
        }

        const ListTag = block.kind === 'ordered' ? 'ol' : 'ul'

        return (
          <ListTag
            key={index}
            className={
              block.kind === 'ordered'
                ? 'ml-5 list-decimal space-y-1 marker:text-subtle'
                : 'ml-5 list-disc space-y-1 marker:text-subtle'
            }
          >
            {block.items.map((item, itemIndex) => (
              <li key={itemIndex}>
                <Fragment>{renderInline(item, `li-${index}-${itemIndex}`)}</Fragment>
              </li>
            ))}
          </ListTag>
        )
      })}
    </div>
  )
}
